import { SxProps } from '@mui/system';
import React, { RefObject, useCallback, useEffect, useState } from 'react';
import { Note } from 'sr-types/lib/search/v1/graphql';
import { V } from '../../../Layout';
import { SECURITY_ACTIONS, useSecurity } from '../../../auth/Security';
import TagsPreview from '../../../components/TagsPreview';
import { ENTITY_CONSTANTS } from '../../../constants/entityConstants';
import { FORM_BUTTONS } from '../../../form/formConstants';
import { useShallowMergeSxProps } from '../../../hooks/useShallowMergeSxProps';
import { useI18n } from '../../../i18n/I18n';
import LexicalRenderer from '../../../lexicalRenderer/LexicalRenderer';
import { useNotesContext } from '../hooks/useNotesContext';
import NoteFormActions from './dataInput/NoteFormActions';
import NoteTagsInput from './dataInput/NoteTagsInput';

const BASE_RENDERER_STYLE = {
    gap: 1,
    px: 1.25,
    py: 1
};

type NoteManagerProps = {
    note: Note;
    ref?: RefObject<HTMLDivElement>;
    collapsed?: boolean;
    onSave?: () => void;
    onDelete?: (noteId: string) => Promise<any>;
    onAssociateStatusNote?: {
        saveFunction?: (note: Note) => void;
        loadingState?: boolean;
        statusNote?: Note;
    };
    sx?: SxProps;
    onTagChangeHandler?: (name: string, value: string[]) => void;
    onChange?: (content: string, title: string) => void;
    isNoteSaving?: boolean;
    onCancelNote?: () => void;
    showNotesTags?: boolean;
    onEditNote: (note: Note) => void;
    onShareClick?: (noteId: string) => void;
    resetEditor?: boolean;
    editingNote?: Note;
    addPendingNoteAction?: (tempId: string, callback: (noteId: string) => void) => void;
    disableAllActions?: boolean;
};

export default function NoteManager({
    note,
    ref,
    collapsed,
    onSave,
    onDelete,
    onAssociateStatusNote,
    sx,
    onTagChangeHandler,
    onChange,
    isNoteSaving,
    onCancelNote,
    showNotesTags,
    onEditNote,
    onShareClick,
    resetEditor,
    editingNote,
    addPendingNoteAction,
    disableAllActions = false
}: NoteManagerProps) {
    const contentPlaceholder = useI18n('notes.placeholder.content');
    const [contentString, setContentString] = useState(note.content);
    const isEditingCurrentNote = Boolean(
        showNotesTags && editingNote && (editingNote.id === note.id || editingNote.reference?.id === note.reference?.id)
    );
    const noteForEditState = isEditingCurrentNote ? editingNote : note;
    const rendererStyle = useShallowMergeSxProps(BASE_RENDERER_STYLE, sx);

    useEffect(() => {
        setContentString(note.content);
    }, [note.content]);

    const handleEditorChange = useCallback(
        (content: string, title?: string) => {
            setContentString(content);
            onChange?.(content, title);
        },
        [onChange]
    );

    const handleCancel = useCallback(() => {
        setContentString(note.content);
        onCancelNote?.();
    }, [note.content, onCancelNote]);

    const handleShare = useCallback(() => {
        onShareClick?.(note.id);
    }, [note.id, onShareClick]);

    const { entity, disableTranslation } = useNotesContext();
    const { allowed: canCommentOnParentEntity } = useSecurity(SECURITY_ACTIONS.COMMENT, {
        entityName: entity
    });
    const { allowed: canShareNote } = useSecurity(SECURITY_ACTIONS.SHARE, {
        entityName: ENTITY_CONSTANTS.note,
        entitySecurity: note?.security
    });

    return (
        <V gap={2} ref={ref}>
            <LexicalRenderer
                note={note}
                contentString={contentString}
                contentPlaceholder={contentPlaceholder}
                entityName={entity}
                onChange={handleEditorChange}
                onEdit={onEditNote}
                onShare={handleShare}
                resetEditor={resetEditor}
                onDelete={(id: string) => onDelete(id)}
                onAssociateStatusNote={onAssociateStatusNote}
                collapsed={collapsed}
                showHeader
                showFooter
                title={note.title}
                updatedOn={note.updatedOn}
                updatedByName={note.updatedByName}
                className={'_note'}
                sx={rendererStyle}
                footerSx={{ m: 0 }}
                CustomFooterComponent={<TagsPreview tags={noteForEditState?.labels ?? []} />}
                isEditable={showNotesTags}
                hideActions={[FORM_BUTTONS.save, FORM_BUTTONS.cancel, FORM_BUTTONS.delete]}
                addPendingNoteAction={addPendingNoteAction}
                disableAllActions={disableAllActions}
                disableTranslation={disableTranslation}
            />
            {showNotesTags && note.id && canCommentOnParentEntity && (
                <V gap={1}>
                    <NoteTagsInput value={noteForEditState?.labels} onChange={onTagChangeHandler} />
                    <NoteFormActions
                        noteId={note.id}
                        onSave={onSave}
                        isSaving={isNoteSaving}
                        onCancel={handleCancel}
                        onShare={handleShare}
                        canShare={canShareNote}
                    />
                </V>
            )}
        </V>
    );
}
